import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Percent, ShoppingBag, ArrowRight, Sparkles } from "lucide-react";

/**
 * /share/partner-shops — Partner Discounts reel (iter184).
 *
 * Reflects the REAL affiliate brands surfaced by PartnerShopsStrip
 * (Trek Expert, Camping World UK, Absolute Snow, FINDRA, Teva UK).
 * If the top-bar brand set ever changes, update the SHOPS array here
 * — or wire this reel to the same `/api/affiliate/brands` endpoint.
 */
const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const LINK = `${PUBLIC_URL}/browse?ref=partner-shops-reel`;

const SHOPS = [
  { name: "Trek Expert",       domain: "trekexpert.co.uk",   perk: "adidas Terrex hiking + trail" },
  { name: "Camping World UK",  domain: "campingworld.co.uk", perk: "Tents, stoves, awnings" },
  { name: "Absolute Snow",     domain: "absolute-snow.co.uk",perk: "Rab · Berghaus · Patagonia" },
  { name: "FINDRA",            domain: "findraclothing.com", perk: "Scottish merino base layers" },
  { name: "Teva UK",           domain: "teva-uk.co.uk",      perk: "Walking sandals · water shoes" },
];

const favicon = (domain) => `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

function Scene({ children, bg, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-[360px] h-[640px] mx-auto flex flex-col justify-between text-white overflow-hidden"
        style={{ background: bg }}
      >
        {children}
      </section>
    </div>
  );
}

export default function PartnerShopsReel() {
  return (
    <div className="min-h-screen bg-[#f7f2ec] py-10 px-4">
      <div className="max-w-5xl mx-auto">
        <h1 className="font-display text-4xl mb-2 text-center">Partner Shops Reel</h1>
        <p className="text-center text-sm text-[#666] mb-8">
          Screen-record each 9:16 scene · post to IG / TikTok / Shorts
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Scene 1 — hook */}
          <Scene label="Scene 1 · Hook" bg="linear-gradient(160deg, #0e0e0e 0%, #2a1a10 100%)">
            <div className="p-6 pt-10">
              <div className="inline-block bg-[#ff4500] text-white text-[10px] px-2 py-1 uppercase tracking-widest font-black mb-6">
                Camp gear · trusted brands
              </div>
              <h2 className="font-display text-[44px] leading-none mb-4">
                Your camp<br/>kit &mdash;<br/><span className="text-[#ff9260]">sorted</span>.
              </h2>
              <p className="text-white/80 text-sm">Five brilliant outdoor shops, one home page — no tab-hopping.</p>
            </div>
            <div className="p-6 text-[11px] mono uppercase tracking-widest text-white/60 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" /> CampCompass · partner strip
            </div>
          </Scene>

          {/* Scene 2 — real brands list */}
          <Scene label="Scene 2 · The line-up" bg="#ff4500">
            <div className="p-6 pt-8">
              <div className="text-[10px] mono uppercase tracking-widest mb-2 text-white/80">
                Real affiliates · live in the app
              </div>
              <h2 className="font-display text-[30px] leading-tight mb-4">
                Every kit box, one tap away.
              </h2>
              <div className="space-y-2">
                {SHOPS.map((s) => (
                  <div key={s.name} className="flex items-center gap-3 bg-white/15 backdrop-blur-sm px-3 py-2">
                    <img
                      src={favicon(s.domain)}
                      alt=""
                      className="w-5 h-5 shrink-0 rounded-sm bg-white/90"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-bold leading-tight">{s.name}</div>
                      <div className="text-[10px] mono uppercase tracking-widest text-white/85 truncate">
                        {s.perk}
                      </div>
                    </div>
                    <Percent className="w-3.5 h-3.5 shrink-0" />
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 text-white text-xs mono uppercase tracking-widest">
              One tap, no signup wall — but sign up &amp; save your kit list.
            </div>
          </Scene>

          {/* Scene 3 — CTA + QR */}
          <Scene label="Scene 3 · Get the codes" bg="linear-gradient(160deg, #0a7f3f 0%, #0e0e0e 100%)">
            <div className="p-6 pt-10">
              <div className="inline-block bg-white text-[#0a7f3f] text-[10px] px-2 py-1 uppercase tracking-widest font-black mb-6">
                Free while we grow
              </div>
              <h2 className="font-display text-[40px] leading-none mb-4">
                Scan.<br/>Kit up.<br/>Camp.
              </h2>
              <p className="text-white/85 text-sm inline-flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" /> Sign up 60s &mdash; unlock everything
              </p>
            </div>
            <div className="p-6 flex flex-col items-center gap-2 pb-10">
              <div className="bg-white p-2"><QRCodeSVG value={LINK} size={130} /></div>
              <div className="text-[10px] mono uppercase tracking-widest text-white/70">camp-compass.uk</div>
              <div className="inline-flex items-center gap-1.5 text-sm font-bold mt-1">
                <ArrowRight className="w-4 h-4" /> Start saving
              </div>
            </div>
          </Scene>
        </div>

        <div className="mt-10 p-5 bg-white border hairline max-w-3xl mx-auto">
          <div className="eyebrow mb-2">Caption</div>
          <p className="text-sm">
            🏕️ Every bit of camp kit, one home page. CampCompass now bundles Trek Expert (adidas Terrex),
            Camping World UK, Absolute Snow (Rab / Berghaus / Patagonia), FINDRA merino &amp; Teva UK sandals
            — right on the top bar of the app. Free to sign up, no card, no catches. Link in bio 👆
          </p>
        </div>
      </div>
    </div>
  );
}
